import { useCallback, useEffect, useRef, useState } from "react";
import { Download, FileText, Loader2, RefreshCw } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/v5/components/ui/checkbox";
import { Label } from "@/v5/components/ui/label";
import * as notesSvc from "@/v5/services/notes.service";
import { toast } from "@/store/toastStore";

interface PrintOptionsDialogProps {
  patientId: string;
  noteIds: string;
  onClose: () => void;
  /** "pdf" uses DomPDF (fast for single notes); "html" for bulk lists. Default "html". */
  mode?: "pdf" | "html";
}

interface PrintOpts {
  attachments: boolean;
  logo: boolean;
  headinginfo: boolean;
  documentDetails: boolean;
}

const DEFAULT_OPTS: PrintOpts = { attachments: true, logo: true, headinginfo: true, documentDetails: true };

function buildFilename(patientId: string, ext: string): string {
  const now = new Date();
  const ts = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
    String(now.getHours()).padStart(2, "0"),
    String(now.getMinutes()).padStart(2, "0"),
    String(now.getSeconds()).padStart(2, "0"),
  ].join("");
  return `notes-${patientId}-${ts}.${ext}`;
}

export function PrintOptionsDialog({ patientId, noteIds, onClose, mode = "html" }: PrintOptionsDialogProps) {
  const [opts, setOpts] = useState<PrintOpts>(DEFAULT_OPTS);
  const [appliedOpts, setAppliedOpts] = useState<PrintOpts>(DEFAULT_OPTS);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const prevUrlRef = useRef<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const fetchIdRef = useRef(0);

  const isPdf = mode === "pdf";
  const fetcher = isPdf ? notesSvc.fetchNotePrintPdf : notesSvc.fetchNotePrintHtml;
  const noteCount = noteIds.split(",").filter(Boolean).length;

  const dirty = opts.attachments !== appliedOpts.attachments
    || opts.logo !== appliedOpts.logo
    || opts.headinginfo !== appliedOpts.headinginfo
    || opts.documentDetails !== appliedOpts.documentDetails;

  const doFetch = useCallback(async (fetchOpts: PrintOpts) => {
    const id = ++fetchIdRef.current;
    setLoading(true);
    console.log(`[print] fetching preview for ${noteCount} notes, mode=${mode}`);
    try {
      const url = await fetcher(patientId, noteIds, {
        attachments: fetchOpts.attachments,
        logo: fetchOpts.logo,
        headinginfo: fetchOpts.headinginfo,
        documentDetails: fetchOpts.documentDetails,
      });
      if (id !== fetchIdRef.current) {
        URL.revokeObjectURL(url);
        return;
      }
      if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
      prevUrlRef.current = url;
      setPreviewUrl(url);
    } catch (err) {
      if (id !== fetchIdRef.current) return;
      console.error(`[print] preview failed`, err);
      toast.error("Failed to generate print preview");
    } finally {
      if (id === fetchIdRef.current) setLoading(false);
    }
  }, [patientId, noteIds, fetcher, noteCount, mode]);

  useEffect(() => {
    void doFetch(appliedOpts);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
    };
  }, []);

  const handleApply = useCallback(() => {
    setAppliedOpts({ ...opts });
    void doFetch(opts);
  }, [opts, doFetch]);

  const handlePdf = useCallback(() => {
    if (!previewUrl) return;
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.print();
  }, [previewUrl]);

  const handleDownloadHtml = useCallback(() => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = buildFilename(patientId, "html");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [previewUrl, patientId]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Print Notes"
      size="xl"
      wrapperClassName="!z-[60]"
      footer={(
        <>
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          <Button size="sm" variant="secondary" onClick={handleDownloadHtml} disabled={!previewUrl || loading}>
            <FileText className="mr-1.5 h-4 w-4" /> Download HTML
          </Button>
          <Button size="sm" onClick={handlePdf} disabled={!previewUrl || loading}>
            <Download className="mr-1.5 h-4 w-4" /> Print / Download PDF
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div className="flex items-end gap-4">
          <div className="flex-1 space-y-2">
            <Label className="text-sm font-medium">Include</Label>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={opts.attachments} onCheckedChange={(v) => setOpts(p => ({ ...p, attachments: !!v }))} /> Attachments
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={opts.logo} onCheckedChange={(v) => setOpts(p => ({ ...p, logo: !!v }))} /> Logo
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={opts.headinginfo} onCheckedChange={(v) => setOpts(p => ({ ...p, headinginfo: !!v }))} /> Heading Info
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={opts.documentDetails} onCheckedChange={(v) => setOpts(p => ({ ...p, documentDetails: !!v }))} /> Document Details
              </label>
            </div>
          </div>
          <Button size="sm" onClick={handleApply} disabled={!dirty || loading} className="shrink-0">
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Apply
          </Button>
        </div>

        {previewUrl ? (
          <div className="relative">
            {loading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded bg-white/70">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            )}
            <iframe
              ref={iframeRef}
              title="Notes preview"
              src={previewUrl}
              className="h-[500px] w-full rounded border border-slate-200 bg-white"
            />
          </div>
        ) : (
          <div className="flex h-[500px] items-center justify-center rounded border border-dashed border-slate-300 bg-slate-50">
            <p className="text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
